package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.error.TableError
import fr.manyverse.app.domain.model.GameTable
import fr.manyverse.app.domain.model.MemberRole
import fr.manyverse.app.domain.model.TableMember
import fr.manyverse.app.domain.model.UniverseConfig
import fr.manyverse.app.domain.repository.GameTableRepository
import fr.manyverse.app.infrastructure.error.mapToDomainException
import fr.manyverse.app.infrastructure.realtime.GameChange
import fr.manyverse.app.infrastructure.realtime.GameChannelEvent
import fr.manyverse.app.infrastructure.realtime.GameRealtimeChannel
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.query.Columns
import io.github.jan.supabase.postgrest.query.filter.PostgrestFilterBuilder
import io.github.jan.supabase.postgrest.rpc
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.channelFlow
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.map
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Clock

private const val TAG = "GameTableRepository"
private const val SESSIONS_TABLE = "game_tables"
private const val MEMBERS_TABLE = "game_table_members"
private const val CREATE_TABLE_FUNCTION = "create_game_table"
private val MEMBERSHIP_COLUMNS = Columns.raw("role, $SESSIONS_TABLE!inner(*)")
// Sans les notes : chaque membre recharge la liste à chaque arrivée, départ ou changement de
// personnage, alors que seules celles du joueur ouvert servent (getMemberNotes)
private val MEMBER_COLUMNS = Columns.list("id", "table_id", "user_id", "character_id", "role", "joined_at")

class SupabaseGameTableRepository(
    private val supabaseClient: SupabaseClient,
    private val gameChannel: GameRealtimeChannel
) : GameTableRepository {

    override suspend fun createTable(
        universeId: String,
        universeConfig: UniverseConfig,
        gameMasterId: String,
        name: String
    ): Result<GameTable> {
        return try {
            // La base tire le code, crée la table et y inscrit le MJ en une transaction : une
            // coupure en cours de route ne laisse pas de table sans MJ. Le MJ est l'utilisateur
            // connecté (auth.uid()), [gameMasterId] le désigne déjà
            val table = supabaseClient.postgrest
                .rpc(
                    CREATE_TABLE_FUNCTION,
                    CreateGameTableParams(
                        universeId = universeId,
                        universeConfig = universeConfig,
                        name = name
                    )
                )
                .decodeAs<GameTable>()

            Result.success(table)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun findTableByCode(code: String): Result<GameTable?> {
        return try {
            val tables = supabaseClient
                .from(SESSIONS_TABLE)
                .select(Columns.ALL) {
                    filter {
                        eq("code", code)
                    }
                }
                .decodeList<GameTable>()

            Result.success(tables.firstOrNull())
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun getTableById(
        tableId: String
    ): Result<GameTable?> {
        return try {
            val tables = supabaseClient
                .from(SESSIONS_TABLE)
                .select(Columns.ALL) {
                    filter {
                        eq("id", tableId)
                    }
                }
                .decodeList<GameTable>()

            Result.success(tables.firstOrNull())
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun deleteTable(
        tableId: String
    ): Result<Unit> {
        return try {
            // Les membres partent avec la table (ON DELETE CASCADE), dans la même transaction : chacun
            // reçoit quand même la suppression de sa ligne par le trigger broadcast_game_change
            supabaseClient
                .from(SESSIONS_TABLE)
                .delete {
                    filter {
                        eq("id", tableId)
                    }
                }

            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun joinTable(
        tableId: String,
        userId: String,
        characterId: String?,
        role: MemberRole
    ): Result<TableMember> {
        return try {
            val member = supabaseClient
                .from(MEMBERS_TABLE)
                .insert(
                    mapOf(
                        "table_id" to tableId,
                        "user_id" to userId,
                        "character_id" to characterId,
                        "role" to role.name
                    )
                ) {
                    select()
                }
                .decodeSingle<TableMember>()

            Result.success(member)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun leaveTable(
        tableId: String,
        userId: String
    ): Result<Unit> {
        return try {
            supabaseClient
                .from(MEMBERS_TABLE)
                .delete {
                    filter {
                        eq("table_id", tableId)
                        eq("user_id", userId)
                    }
                }

            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun kickMember(
        tableId: String,
        userId: String
    ): Result<Unit> {
        return leaveTable(tableId, userId)
    }

    private suspend fun getTableMembers(
        tableId: String
    ): Result<List<TableMember>> {
        return try {
            val members = supabaseClient
                .from(MEMBERS_TABLE)
                .select(MEMBER_COLUMNS) {
                    filter {
                        eq("table_id", tableId)
                    }
                }
                .decodeList<TableMember>()

            Result.success(members)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override fun observeTableMembers(
        tableId: String
    ): Flow<List<TableMember>> = channelFlow {
        var loaded = false
        gameChannel.events(MEMBERS_TABLE).collect { event ->
            // La liste complète est relue à chaque changement de cette table et à chaque reprise de
            // l'écoute : les arrivées et départs survenus pendant une coupure ne sont pas rejoués
            if (event is GameChannelEvent.Change &&
                extractString(event.change.row?.get("table_id")) != tableId
            ) {
                return@collect
            }
            val members = getTableMembers(tableId).getOrElse { error ->
                // Échec du chargement initial : le flux se termine en erreur plutôt que d'annoncer
                // une table sans joueurs (déjà remonté par le mapper). Ensuite, la liste déjà connue
                // reste affichée : une liste vide ferait disparaître tous les joueurs de l'écran
                if (!loaded) throw error
                AppLogger.warn(TAG, "Failed to refresh table members for table=$tableId")
                return@collect
            }
            loaded = true
            send(members)
        }
    }

    override fun observeUserMembershipChanges(userId: String): Flow<Unit> =
        gameChannel.events(MEMBERS_TABLE)
            .filter { event ->
                // Les changements survenus pendant une coupure ne sont pas rejoués : chaque reprise
                // compte. Une modification de sa ligne (personnage, notes) ne change pas ses tables
                event !is GameChannelEvent.Change ||
                    (event.change.type != GameChange.Type.UPDATE &&
                        extractString(event.change.row?.get("user_id")) == userId)
            }
            .map { }

    /**
     * Tables de [userId] avec son rôle, en une requête : PostgREST joint chaque ligne de membre à
     * sa table (clé étrangère table_id). `!inner` écarte les lignes dont la table n'est pas lisible.
     */
    private suspend fun getMemberships(
        userId: String,
        filters: PostgrestFilterBuilder.() -> Unit = {}
    ): List<Pair<GameTable, MemberRole>> =
        supabaseClient
            .from(MEMBERS_TABLE)
            .select(MEMBERSHIP_COLUMNS) {
                filter {
                    eq("user_id", userId)
                    filters()
                }
            }
            .decodeList<MembershipDto>()
            .map { it.table to it.role }

    override suspend fun getUserActiveTables(
        universeId: String,
        userId: String
    ): Result<List<Pair<GameTable, MemberRole>>> {
        return try {
            Result.success(getMemberships(userId) { eq("$SESSIONS_TABLE.universe_id", universeId) })
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun getAllUserActiveTables(
        userId: String
    ): Result<List<Pair<GameTable, MemberRole>>> {
        return try {
            Result.success(getMemberships(userId))
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun getActiveTableForCharacter(
        characterId: String,
        userId: String
    ): Result<GameTable?> {
        return try {
            val table = getMemberships(userId) { eq("character_id", characterId) }
                .firstOrNull()
                ?.first
            Result.success(table)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun updateMemberCharacter(
        tableId: String,
        userId: String,
        characterId: String
    ): Result<Unit> {
        return try {
            supabaseClient
                .from(MEMBERS_TABLE)
                .update(
                    mapOf(
                        "character_id" to characterId,
                        // Signal pour le MJ : la base remplace la valeur par son heure
                        "character_updated_at" to Clock.System.now().toString()
                    )
                ) {
                    filter {
                        eq("table_id", tableId)
                        eq("user_id", userId)
                    }
                }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun touchMemberCharacter(
        tableId: String,
        userId: String
    ): Result<Unit> {
        return try {
            // Signal pour le MJ : la base remplace la valeur par son heure, l'app ne fait que la changer
            supabaseClient
                .from(MEMBERS_TABLE)
                .update(mapOf("character_updated_at" to Clock.System.now().toString())) {
                    filter {
                        eq("table_id", tableId)
                        eq("user_id", userId)
                    }
                }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun updateTableName(
        tableId: String,
        name: String
    ): Result<Unit> {
        return try {
            supabaseClient
                .from(SESSIONS_TABLE)
                .update(mapOf("name" to name)) {
                    filter {
                        eq("id", tableId)
                    }
                }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun getMemberNotes(
        tableId: String,
        userId: String
    ): Result<String> {
        return try {
            val member = supabaseClient
                .from(MEMBERS_TABLE)
                .select(Columns.list("notes")) {
                    filter {
                        eq("table_id", tableId)
                        eq("user_id", userId)
                    }
                }
                .decodeSingleOrNull<MemberNotesDto>()
                ?: return Result.failure(TableError("Table member not found"))
            Result.success(member.notes)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun updateMemberNotes(
        tableId: String,
        userId: String,
        notes: String
    ): Result<Unit> {
        return try {
            supabaseClient
                .from(MEMBERS_TABLE)
                .update(mapOf("notes" to notes)) {
                    filter {
                        eq("table_id", tableId)
                        eq("user_id", userId)
                    }
                }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "table"))
        }
    }
}

@Serializable
private data class CreateGameTableParams(
    @SerialName("p_universe_id") val universeId: String,
    @SerialName("p_universe_config") val universeConfig: UniverseConfig,
    @SerialName("p_name") val name: String
)

@Serializable
private data class MembershipDto(
    @SerialName("role") val role: MemberRole,
    @SerialName(SESSIONS_TABLE) val table: GameTable
)

@Serializable
private data class MemberNotesDto(
    @SerialName("notes") val notes: String
)
